import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { ConfirmationService } from "primeng/api";
import { FileUpload } from 'primeng/fileupload';
import { BrandService } from '../../services/config/brand.service';
import { UserContextService } from '../../services/safeguard/user-context.service';
import { SharedService } from "../../shared/services/shared.service";
import { IBrand } from '../../shared/interfaces/brand';
import { IViewableDocument } from '../../interfaces/viewable-document';

@Component({
  selector: 'app-viewable-documents',
  providers: [DecimalPipe],
  standalone: false,
  // imports: [],
  templateUrl: './viewable-documents.component.html',
  styleUrl: './viewable-documents.component.css'
})
export class ViewableDocumentsComponent implements OnInit {

  @ViewChild('fileUploadControl') public fileUploadControl: any;

  private readonly confirmationService = inject(ConfirmationService);
  private readonly clipboard = inject(Clipboard);
  private readonly brandService = inject(BrandService);
  private readonly sharedService = inject(SharedService);
  private readonly userContextService = inject(UserContextService);
  private readonly decimalPipe = inject(DecimalPipe);

  titleMessage: string = "Viewable Documents";
  currentBrand: IBrand;  
  viewableDocumentFile!: File | null;
  viewableDocuments: any[] = [];
  cols!: any[];
  documentScope: string = "BRAND";
  
  isSpinnerHidden: boolean = true;    
  openBrandSelectionDialog: boolean = false;
  isCurrentBrandShown: boolean = true;
  isSubmitShown: boolean = false;
  displayAddDialog: boolean = false;

  constructor(
  ) { 
    this.currentBrand = this.sharedService.getDummyBrand();    
    this.cols = [
      { field: 'documentName', header: 'Document Name', display: 'table-cell', width:'20%' },
      { field: 'documentUrl', header: 'Document Url', display: 'table-cell', width:'38%' },
      { field: 'lastModifiedDate', header: 'Last Mofified Date', display: 'table-cell', width:'15%' },
      { field: 'documentSize', header: 'Size', display: 'table-cell', width:'7%', formatter: (row: any) => this.formatNumber(row.documentSize) },
      { field: 'brandCode', header: 'Scope', display: 'table-cell', width:'8%' },
    ]; 
  }  

  ngOnInit(): void {
    if (this.sharedService.isCurrentBrandKnown()) {
      this.currentBrand = this.sharedService.getCurrentBrand();
      this.titleMessage = this.sharedService.makePageTitle (this.currentBrand, 'Viewable Documents');
      this.openBrandSelectionDialog = false;
      this.getViewableDocuments(this.currentBrand.brandCode);
    } else {
      this.isCurrentBrandShown = false;
      this.openBrandSelectionDialog = true;
    }    
      // Hide the submit button for users with read-only access type
    if (this.userContextService.userHasPrivilege('PRIV_WP_VIEWABLE_DOCUMENT_MODIFY')) {
      this.isSubmitShown = true;
    }
  }

  // When the user opens up the dialog to select a brand 
  onChooseBrandClick(event: any) {
    this.openBrandSelectionDialog = true;
  }

  // When a brand is chosen
  chooseBrand(item: IBrand) {
    this.openBrandSelectionDialog = false;
    if (item != null) {
      this.isCurrentBrandShown = false;
      this.currentBrand = item;
      this.titleMessage = this.sharedService.makePageTitle (this.currentBrand, 'Viewable Documents');
      this.sharedService.setCurrentBrand(item);
      this.getViewableDocuments(this.currentBrand.brandCode);
    }
  }

  // Get a list of viewable documents stored in a S3 bucket
  getViewableDocuments(brandCode: string): void {
    this.isSpinnerHidden = false;
    this.viewableDocuments = [];
    this.brandService.getViewableDocuments(brandCode) 
    .subscribe({
      next: (response) => {
        this.viewableDocuments = response; 
        this.isSpinnerHidden = true;
      },
      error: (error) => {
        },
      complete: () => {
      }
    });
  }

  // When the user selects a new document
  onViewableDocumentFileAdd(event: any) {
    this.viewableDocumentFile = event.files[0]
  }   

  // WHen the user removes a document from the File Upload control
  onViewableDocumentFileRemove(event: any) {
    this.viewableDocumentFile = null
  }   

  // Upload a new viewable document to S3
  onAddViewableDocument(event: any) {
    // Call the service to invoke a Web API call
    this.isSpinnerHidden = false;
    this.brandService.addViewableDocument(this.documentScope == "BRAND" ? this.currentBrand.brandCode : "common", this.viewableDocumentFile)
    .subscribe({
      next: (response) => {
        // console.log('response', response);
        this.getViewableDocuments(this.currentBrand.brandCode);
        this.isSpinnerHidden = true;
        this.displayAddDialog = false;
        this.alertTheUser("Processed", response.message);
      },
      error: (error) => {
        console.log('error',error);
        this.isSpinnerHidden = true;
        this.alertTheUser("Error", error.message);
      },
      complete: () => {
      }
    });

  }

  // Show the Add new document dialog
  showDialogToAdd() {
    this.documentScope = "BRAND";
    this.viewableDocumentFile = null;
    this.fileUploadControl.clear();
    this.displayAddDialog = true;
  }

  // Hide the Add new document dialog
  cancelDialogToAdd() {
    this.displayAddDialog = false;
  }

  // Copy the Url of a selected document to the clipboard
  onRowCopy(event: any) {
    this.clipboard.copy(event.documentUrl);
  }

  // Delete viewable document
  onRowDelete(event: any) {
    // Make sure that the user indeed wants to delete the document
    this.confirmationService.confirm({
      message: 'Are you sure that you really want to delete the document "' + event.documentName + '" ?',
      header: 'Confirmation',
      key: 'viewable-documents',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Yes",
      rejectVisible: true,
      acceptButtonStyleClass: "p-button-success wal-success  p-button-rounded",
      rejectButtonStyleClass: "p-button-danger wal-danger p-button-rounded",   
      accept: () => {
        // Call the service to invoke a Web API call
        this.brandService.deleteViewableDocument(event.brandCode, event.documentName)
            .subscribe({
              next: (response) => {
                this.alertTheUser("Processed", "Successfully deleted document " + event.documentName);
                this.getViewableDocuments(this.currentBrand.brandCode);
              },
              error: (error) => {
                this.alertTheUser("Error", error.message);
              },
              complete: () => {
              }
            });
      },
      reject: () => {
      }
    });
  }

  // Revome the file from p-fileUpload control
  // removeFileFromSelection() {
  //   const control: FileUpload = this.fileUploadControl as FileUpload;

  //   // Assuming you want to remove the first file for demonstration
  //   if (control.files && control.files.length > 0) {
  //     const fileToRemove = control.files[0];
  //     const idx = control.files.indexOf(fileToRemove);
  //     control.remove(null, idx); // Pass null for the event, and the file index
  //   }
  // }

  // Popup an alert dialog
  alertTheUser(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      key: 'viewable-documents',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info  p-button-rounded",
      accept: () => {
         return;
       }
    });
  }

  // Format the number with a thousands separator
  formatNumber(value: number): string | null {
    return this.decimalPipe.transform(value, '1.0-0'); // e.g., 1234567 → "1,234,567"
  }  


}
